import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../utils/platform';
import { Link, useNavigate } from 'react-router-dom';
import { getCR, createCR, updateCRStatus, deleteCR, getFundCategories, createFundCategory, getMembers, getChurchProfile, getUsers, getMemberOutstanding } from '../../services/api';
import { FiPlus, FiTrash2, FiX, FiBook, FiPrinter, FiShare2, FiEye } from 'react-icons/fi';
import Toast from '../../components/Toast';
import { useAuth } from '../../context/AuthContext';
import { printCashReceipt, printCRList, shareCashReceipt } from '../../utils/printReceipt';
import useAppRefresh from '../../hooks/useAppRefresh';

const crStatusPill = { pending: 'pill-yellow', approved: 'pill-green', rejected: 'pill-red' };

const CR = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [list, setList] = useState([]);
  const [funds, setFunds] = useState([]);
  const [members, setMembers] = useState([]);
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [fundFilter, setFundFilter] = useState('');
  // v1.9.123 — tokenised Received From filter. Mirrors the new-CR split
  // search so "feqade sl" matches "Feqade Slasie" even in Received From
  // (free-text) rows. Pure client-side over the already-loaded list.
  const [nameFilter, setNameFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    member_id: '', received_from: '', spiritual_father_name: '',
    hagest_sbket: '', city: '',
    amounts: {},
    // Per-currency method picks how an amount gets split into the 5 buckets.
    // 'usd' → 'cash' | 'bank'  (no mobile money in USD per design)
    // 'k'   → 'cash' | 'bank' | 'momo'
    methods: { usd: 'cash', k: 'cash' },
    date: new Date().toISOString().slice(0, 10),
    fund_category_id: '', payment_method: 'cash', notes: '',
    // v1.6.0 — when ticked, this CR's amounts are FIFO-applied against the
    // member's outstanding-debt entries. Only shows in the UI when the
    // selected member actually has an unpaid balance.
    apply_to_outstanding: false,
  });
  // v1.6.0 — outstanding balance for the currently-selected member (member_id
  // resolves when the paidFrom autocomplete is picked).
  const [pickedOutstanding, setPickedOutstanding] = useState({ usd: 0, kwacha: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  // Inline new type
  const [showNewType, setShowNewType] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [savingType, setSavingType] = useState(false);
  // Single input with tokenised matching — mirrors the All Members search
  // (v1.9.12): "DANI BE" matches "DANIEL BEKELE" because each whitespace
  // token has to appear in some column. Simpler UX than two fields and it
  // handles first/father/grandfather/phone/card without the user picking a
  // field first.
  const [memberSearch,   setMemberSearch]   = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [profile, setProfile] = useState({});
  const [allUsers, setAllUsers] = useState([]);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const fetchFunds = () => getFundCategories().then(r => setFunds(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  const fetch = () => getCR({ from, to, fund_id: fundFilter }).then(r => setList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useAppRefresh(() => { fetch(); fetchFunds(); });

  useEffect(() => {
    fetch();
    fetchFunds();
    getMembers({}).then(r => setMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getChurchProfile().then(r => {
      setProfile(r.data || {});
      setForm(p => ({
        ...p,
        hagest_sbket: r.data?.hagest_sbket || '',
        city: r.data?.city || '',
      }));
    }).catch(() => {});
    getUsers().then(r => setAllUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [from, to, fundFilter]);

  const usersByRole = (role) => allUsers
    .filter(u => u.role === role && u.active)
    .map(u => u.name)
    .join(', ');

  const buildReceiptPayload = (r) => ({
    cr_number: r.cr_number,
    date: r.date,
    received_from: r.member_name?.trim() || r.received_from || '',
    member_card: r.member_card || '',
    spiritual_father_name: r.spiritual_father_name || '',
    hagest_sbket: r.hagest_sbket || '',
    city: r.city || '',
    fund_name: r.fund_name || '',
    payment_method: r.payment_method || '',
    amounts: r.amounts || {},
    notes: r.notes || '',
    received_by_name: r.created_by_name || '',
    verify_token: r.verify_token || '',
  });
  const reprintReceipt = (r) => printCashReceipt(buildReceiptPayload(r), profile);
  const shareReceipt = async (r) => {
    try {
      showToast('Preparing receipt to share…');
      const res = await shareCashReceipt(buildReceiptPayload(r), profile);
      if (res?.shared === 'downloaded') {
        showToast(`PDF downloaded (${res.filename}) — attach it in WhatsApp/email`);
      } else if (res?.shared === 'needs-tap') {
        showToast('Receipt ready — tap Share again');
      } else if (res?.shared === 'cancelled') {
        // silent — user cancelled the share sheet
      }
    } catch (e) {
      showToast(e?.message || 'Could not share the receipt', 'error');
    }
  };

  // Tokenised match — each whitespace-separated token from the search box
  // must appear somewhere across first/middle/last/phone/card. So "SI SEL"
  // matches "SIRAK SELOMON ADHANOM"; "DANI BE" matches "DANIEL BEKELE".
  const suggestions = React.useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return [];
    const tokens = q.split(/\s+/).filter(Boolean);
    return members.filter(m => {
      const hay = [m.first_name, m.middle_name, m.last_name, m.phone, m.card_number, m.christian_name]
        .filter(Boolean).join(' ').toLowerCase();
      return tokens.every(t => hay.includes(t));
    }).slice(0, 12);
  }, [memberSearch, members]);

  const handleSearchChange = (val) => {
    setMemberSearch(val);
    setShowSuggestions(true);
    // Free-text (non-member) case: keep received_from in sync so the CR
    // still saves cleanly even if the user never picks a suggestion.
    setForm(p => ({ ...p, received_from: val, member_id: '', spiritual_father_name: '' }));
  };

  const clearSelection = () => {
    setForm(p => ({ ...p, member_id: '', received_from: '', spiritual_father_name: '', apply_to_outstanding: false }));
    setPickedOutstanding({ usd: 0, kwacha: 0 });
    setMemberSearch('');
  };

  const selectSuggestion = (member) => {
    const fullName = `${member.first_name} ${member.middle_name || ''} ${member.last_name}`.trim().replace(/\s+/g, ' ');
    setForm(p => ({
      ...p,
      received_from: fullName,
      member_id: member.id,
      spiritual_father_name: member.spiritual_father_name || '',
      apply_to_outstanding: false, // reset — user re-ticks after seeing the balance
    }));
    setMemberSearch('');
    setShowSuggestions(false);
    // v1.6.0 — fetch this member's outstanding balance so the settle checkbox
    // knows whether to show and what to display.
    getMemberOutstanding(member.id).then(r => {
      setPickedOutstanding(r.data?.balance || { usd: 0, kwacha: 0 });
    }).catch(() => setPickedOutstanding({ usd: 0, kwacha: 0 }));
  };

  // Which fund is currently selected in the modal (used to hide payer name
  // when the fund is anonymous — ሞባእ / tray-offering style collections).
  const selectedFund = React.useMemo(
    () => funds.find(f => String(f.id) === String(form.fund_category_id)) || null,
    [funds, form.fund_category_id]
  );
  const isAnonymousFund = !!selectedFund?.is_anonymous;
  // v1.9.128 — "Others" fund (Others / ካልእ ኣጋዋ / ሌላ ገቢ) needs a reason, so
  // the CR row isn't a bare "Others" with no explanation of what it was for.
  // Match on the English or either Ge'ez label since the fund can be renamed.
  const isOthersFund = !!selectedFund && /other|ካልእ|ሌላ/i.test(selectedFund.name || '');

  const handleAdd = async () => {
    setError('');
    if (!form.date) return setError(t('cr.dateRequired'));
    if (!form.fund_category_id) return setError(t('cr.typeRequired'));
    // v1.9.128 — Reason mandatory for "Others" fund.
    if (isOthersFund && !String(form.notes || '').trim()) {
      return setError(t('cr.reasonRequiredForOthers', { defaultValue: 'Please specify what this "Others" payment is for.' }));
    }
    // Payer name is required EXCEPT when the fund itself is anonymous.
    // For anonymous funds we auto-stamp received_from with the fund label so
    // the CR list, Cash Book, and receipt still have something readable.
    let effectiveReceivedFrom = form.received_from?.trim();
    if (isAnonymousFund) {
      if (!effectiveReceivedFrom) effectiveReceivedFrom = selectedFund.name;
    } else if (!effectiveReceivedFrom) {
      return setError(t('cr.paidFromRequired'));
    }
    const cleanAmounts = Object.fromEntries(Object.entries(form.amounts || {}).filter(([, v]) => parseFloat(v) > 0).map(([k, v]) => [k, parseFloat(v)]));
    if (Object.keys(cleanAmounts).length === 0) return setError(t('cr.amountRequired'));
    // Split each currency total into the 5 buckets using the picked method.
    const usd = parseFloat(cleanAmounts['$'] || 0) || 0;
    const k   = parseFloat(cleanAmounts['K'] || 0) || 0;
    const buckets = { usd_cash: 0, usd_bank: 0, k_cash: 0, k_bank: 0, k_momo: 0 };
    if (usd > 0) buckets[form.methods.usd === 'bank' ? 'usd_bank' : 'usd_cash'] = usd;
    if (k > 0) {
      const m = form.methods.k;
      buckets[m === 'bank' ? 'k_bank' : m === 'momo' ? 'k_momo' : 'k_cash'] = k;
    }
    setSaving(true);
    try {
      const res = await createCR({ ...form, received_from: effectiveReceivedFrom, amounts: cleanAmounts, buckets, apply_to_outstanding: !!form.apply_to_outstanding });
      const submittedForm = { ...form, received_from: effectiveReceivedFrom, amounts: cleanAmounts };
      const fundName = funds.find(f => String(f.id) === String(submittedForm.fund_category_id))?.name || '';
      setShowAdd(false);
      setForm({ member_id: '', received_from: '', spiritual_father_name: '', hagest_sbket: profile.hagest_sbket || '', city: profile.city || '', amounts: {}, methods: { usd: 'cash', k: 'cash' }, date: new Date().toISOString().slice(0, 10), fund_category_id: '', payment_method: 'cash', notes: '' });
      setMemberSearch('');
      showToast(res.data?.status === 'pending' ? 'Cash receipt created — awaiting cashier approval' : t('cr.crCreated'));
      // Auto-print only on desktop; phones have no printer and the dialog is disruptive (reprint button is always available).
      if (window.innerWidth > 768) {
        printCashReceipt({
          cr_number: res.data.cr_number,
          date: submittedForm.date,
          received_from: submittedForm.received_from,
          spiritual_father_name: submittedForm.spiritual_father_name,
          hagest_sbket: submittedForm.hagest_sbket,
          city: submittedForm.city,
          fund_name: fundName,
          payment_method: submittedForm.payment_method,
          amounts: submittedForm.amounts,
          notes: submittedForm.notes,
          received_by_name: user?.name || '',
          verify_token: res.data?.verify_token || '',
        }, profile);
      }
      fetch();
    } catch (e) { setError(e.response?.data?.error || t('myProfile.failed')); }
    finally { setSaving(false); }
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) return;
    setSavingType(true);
    try {
      const res = await createFundCategory({ name: newTypeName.trim() });
      await fetchFunds();
      setForm(p => ({ ...p, fund_category_id: String(res.data.id) }));
      setNewTypeName('');
      setShowNewType(false);
      showToast(t('cr.newTypeAdded'));
    } catch (e) { showToast(t('myProfile.failed'), 'error'); }
    finally { setSavingType(false); }
  };

  // v1.9.25 — deletion now requires the OTHER role's approval. Prompt for a
  // reason and open a request rather than deleting outright.
  const handleDelete = async (r) => {
    if (r.pending_deletion_request_id) {
      showToast('A deletion request for this CR is already pending approval.', 'error');
      return;
    }
    const reason = window.prompt(`Request deletion of ${r.cr_number}? Please write a reason (required):`);
    if (!reason || !reason.trim()) return;
    try {
      const res = await deleteCR(r.id, reason.trim());
      showToast(res.data?.message || 'Deletion request submitted for approval.');
      fetch();
    } catch (e) {
      showToast(e.response?.data?.error || 'Could not submit request', 'error');
    }
  };

  const handleCRStatus = async (r, status) => {
    try {
      await updateCRStatus(r.id, { status });
      showToast(status === 'approved' ? 'Cash receipt approved' : 'Cash receipt rejected');
      fetch();
    } catch (e) { showToast(e.response?.data?.error || 'Action failed', 'error'); }
  };

  const currencies = (profile.currencies && profile.currencies.length) ? profile.currencies : ['$', 'K'];
  // v1.9.123 — tokenised Received From filter. Splits the search text on
  // whitespace; every token must appear in Received From OR Spiritual Father
  // (same loose matching the new-CR autocomplete uses).
  const nameTokens = String(nameFilter || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filteredList = nameTokens.length === 0 ? list : list.filter(r => {
    const hay = [r.member_name, r.received_from, r.spiritual_father_name]
      .filter(Boolean).join(' ').toLowerCase();
    return nameTokens.every(t => hay.includes(t));
  });
  // Per-currency totals over the FILTERED list — what you see is what you count.
  const totalsByCurrency = currencies.reduce((acc, sym) => {
    acc[sym] = filteredList.reduce((s, r) => s + (parseFloat((r.amounts || {})[sym]) || 0), 0);
    return acc;
  }, {});
  const fmt = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1>{t('nav.cr')}</h1>
          <p>
            {t('common.total')}: {currencies.map((sym, i) => (
              totalsByCurrency[sym] > 0 ? (
                <span key={sym} style={{ marginLeft: i === 0 ? 0 : 12 }}>
                  <strong>{sym}{fmt(totalsByCurrency[sym])}</strong>
                </span>
              ) : null
            ))}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isMobile && <button className="btn btn-secondary" onClick={() => printCRList({
            records: filteredList,
            profile,
            from,
            to,
            filterLabel: [
              fundFilter ? funds.find(f => String(f.id) === String(fundFilter))?.name : '',
              nameFilter ? `Name: ${nameFilter}` : '',
            ].filter(Boolean).join(' · '),
            preparedBy: user?.name || '',
            financeName: usersByRole('finance'),
            cashierName: usersByRole('cashier'),
          })} disabled={filteredList.length === 0}>
            <FiPrinter /> {t('recordAttendance.print')}
          </button>}
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FiPlus /> {t('form.newCR')}</button>
        </div>
      </div>

      <div className="filters-row">
        <label style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('table.from')}</label>
        <input className="filter-input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <label style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('table.to')}</label>
        <input className="filter-input" type="date" value={to} onChange={e => setTo(e.target.value)} />
        <select className="filter-select" value={fundFilter} onChange={e => setFundFilter(e.target.value)}>
          <option value="">{t('common.all')}</option>
          {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        {/* v1.9.123 — tokenised search over Received From + Spiritual Father.
            Each whitespace token must appear in the row — same loose match
            the new-CR autocomplete uses. */}
        <input
          className="filter-input"
          type="search"
          value={nameFilter}
          onChange={e => setNameFilter(e.target.value)}
          placeholder={t('finance.receivedFrom') + '…'}
          style={{ minWidth: 180 }}
        />
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>#</th><th>{t('finance.crNumber')}</th><th>{t('common.date')}</th><th>{t('finance.receivedFrom')}</th><th>{t('members.spiritualFather')}</th>
              <th>{t('finance.diocese')}</th><th>{t('finance.city')}</th><th>{t('finance.fund')}</th><th>{t('finance.paymentMethod')}</th>{currencies.map(sym => (<th key={sym}>Amount {sym}</th>))}<th>{t('table.by')}</th><th>{t('common.status')}</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filteredList.length === 0 ? (
              <tr><td colSpan={12 + currencies.length}><div className="empty-state"><div className="empty-icon"><FiBook /></div><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : filteredList.map((r, i) => {
              const st = r.status || 'approved';
              return (
              <tr key={r.id}>
                <td data-label="#">{i + 1}</td>
                <td data-label={t('finance.crNumber')}>
                  <Link to={`/finance/cr/${r.cr_number}`} style={{ color: '#4f46e5', fontWeight: 700, textDecoration: 'none' }} title="View this receipt">
                    {r.cr_number}
                  </Link>
                </td>
                <td data-label={t('common.date')}>
                  <div>{r.date}</div>
                  {(r.approved_at || r.created_at) && (
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                      {String(r.approved_at || r.created_at).slice(11, 16)}
                    </div>
                  )}
                </td>
                <td data-label={t('finance.receivedFrom')}>{r.member_name?.trim() || r.received_from || '—'}</td>
                <td data-label={t('members.spiritualFather')}>{r.spiritual_father_name || '—'}</td>
                <td data-label={t('finance.diocese')}>{r.hagest_sbket || '—'}</td>
                <td data-label={t('finance.city')}>{r.city || '—'}</td>
                <td data-label={t('finance.fund')}>{r.fund_name || '—'}</td>
                <td data-label={t('finance.paymentMethod')} style={{ textTransform: 'capitalize' }}>{r.payment_method?.replace('_', ' ') || '—'}</td>
                {currencies.map(sym => {
                  const v = parseFloat((r.amounts || {})[sym] || 0);
                  return (
                    <td key={sym} data-label={`Amount ${sym}`}>
                      <strong style={{ color: v > 0 ? '#16a34a' : '#9ca3af' }}>{v > 0 ? `${sym}${fmt(v)}` : '—'}</strong>
                    </td>
                  );
                })}
                <td data-label={t('table.by')}>{r.created_by_name || '—'}</td>
                <td data-label={t('common.status')}>
                  <span className={`pill ${crStatusPill[st] || 'pill-gray'}`}>{st}</span>
                  {r.pending_deletion_request_id && (
                    <span title="Deletion requested — waiting for opposite-role approval" style={{ marginLeft: 6, display: 'inline-block', padding: '2px 8px', borderRadius: 999, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', fontSize: 11, fontWeight: 700 }}>
                      ⏳ Delete pending
                    </span>
                  )}
                </td>
                <td data-label="Actions">
                  <div className="action-btns">
                    {st === 'pending' && user?.role === 'cashier' && (
                      <>
                        <button className="btn btn-sm btn-success" onClick={() => handleCRStatus(r, 'approved')}>{t('approve.approveBtn')}</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleCRStatus(r, 'rejected')}>{t('approve.rejectBtn')}</button>
                      </>
                    )}
                    {!isMobile && (
                      <button className="btn-icon" title={t('recordAttendance.print')} onClick={() => reprintReceipt(r)}><FiPrinter /></button>
                    )}
                    <button
                      className="btn-icon"
                      title="View this receipt"
                      onClick={() => navigate(`/finance/cr/${r.cr_number}`)}
                      style={{ color: '#4f46e5' }}
                    ><FiEye /></button>
                    <button className="btn-icon" title="Share receipt (PDF file)" onClick={() => shareReceipt(r)}><FiShare2 /></button>
                    <button
                      className="btn-icon danger"
                      onClick={() => handleDelete(r)}
                      title={r.pending_deletion_request_id ? 'Deletion already requested' : 'Request deletion'}
                      disabled={!!r.pending_deletion_request_id}
                      style={r.pending_deletion_request_id ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                    ><FiTrash2 /></button>
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
          {list.length > 0 && (
            <tfoot>
              <tr style={{ background: '#f9fafb', fontWeight: 700 }}>
                <td colSpan={9} style={{ padding: '10px 16px' }}>{t('common.total')}</td>
                {currencies.map(sym => (
                  <td key={sym} style={{ padding: '10px 16px', color: '#16a34a' }}>{sym}{fmt(totalsByCurrency[sym] || 0)}</td>
                ))}
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* New CR Modal */}
      {showAdd && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 620 }}>
            <div className="modal-header">
              <div><h3>{t('cr.newCashReceipt')}</h3></div>
              <button className="btn-icon" onClick={() => { setShowAdd(false); setShowNewType(false); }}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              <div className="form-grid">

                {/* Paid From — split search (First name + Father's name).
                    Once a member is picked the two inputs collapse into a
                    locked chip; the X clears and re-opens the search.
                    Hidden entirely when the selected fund is anonymous (ሞባእ). */}
                {isAnonymousFund ? (
                  <div className="form-group form-full">
                    <div style={{
                      padding: '10px 14px', background: '#fef3c7',
                      border: '1px solid #fcd34d', borderRadius: 8,
                      fontSize: 13, color: '#92400e'
                    }}>
                      <strong>Anonymous collection —</strong> no payer name required.
                      Will be recorded under &quot;<strong>{selectedFund.name}</strong>&quot;.
                    </div>
                  </div>
                ) : (
                <div className="form-group form-full" style={{ position: 'relative' }}>
                  <label>{t('cr.paidFrom')} <span className="required">*</span></label>

                  {form.member_id ? (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 14px', background: '#eef2ff',
                      border: '1px solid #c7d2fe', borderRadius: 8,
                    }}>
                      <span style={{ color: '#4f46e5', fontSize: 12, fontWeight: 700 }}>✓</span>
                      <span style={{ color: '#1f2937', fontWeight: 600, flex: 1 }}>{form.received_from}</span>
                      <button type="button" onClick={clearSelection}
                        style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}
                        title="Clear selection"><FiX size={16} /></button>
                    </div>
                  ) : (
                    <>
                      <input
                        value={memberSearch}
                        onChange={e => handleSearchChange(e.target.value)}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                        placeholder={t('cr.paidFromPlaceholder', { defaultValue: 'Type name — "SI SEL" matches SIRAK SELOMON' })}
                        autoComplete="off"
                      />
                      {showSuggestions && suggestions.length > 0 && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                          marginTop: 4,
                          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto'
                        }}>
                          {suggestions.map(m => (
                            <div
                              key={m.id}
                              onMouseDown={() => selectSuggestion(m)}
                              style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              <div style={{ fontWeight: 600, fontSize: 13 }}>
                                {m.first_name} {m.middle_name || ''} {m.last_name}
                              </div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                                {[m.phone, m.spiritual_father_name ? `${t('cr.fatherShort')}: ${m.spiritual_father_name}` : null].filter(Boolean).join(' · ')}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {memberSearch && suggestions.length === 0 && (
                        <div style={{ fontSize: 12, color: '#6b7280', padding: '8px 2px 0' }}>
                          {t('cr.noMemberMatch', { defaultValue: 'No member matches — will be saved as' })}{' '}
                          <strong style={{ color: '#1f2937' }}>{form.received_from || '—'}</strong>
                        </div>
                      )}
                    </>
                  )}
                </div>
                )}

                {/* Spiritual Father — hidden for anonymous funds since no
                    payer means no spiritual father to record. */}
                {!isAnonymousFund && (
                <div className="form-group form-full">
                  <label>{t('members.spiritualFather')} <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11 }}>{t('cr.spiritualFatherAutoHint')}</span></label>
                  <input value={form.spiritual_father_name} onChange={set('spiritual_father_name')} placeholder={t('cr.spiritualFatherPlaceholder')} style={{ background: form.spiritual_father_name ? '#eef2ff' : undefined, color: '#4f46e5', fontWeight: form.spiritual_father_name ? 600 : 400 }} />
                </div>
                )}

                {/* Amounts — one input per configured currency, each with its
                    own method dropdown that decides which bucket the amount
                    lands in (USD: Cash/Bank, K: Cash/Bank/Mobile Money). */}
                {currencies.map(sym => {
                  const isUSD = sym === '$';
                  const methodKey = isUSD ? 'usd' : 'k';
                  const methodOptions = isUSD
                    ? [{ v: 'cash', l: t('finance.cash') }, { v: 'bank', l: t('finance.bank') }]
                    : [{ v: 'cash', l: t('finance.cash') }, { v: 'bank', l: t('finance.bank') }, { v: 'momo', l: t('finance.mobile') }];
                  return (
                    <div className="form-group form-full" key={sym}>
                      <label>Amount {sym}</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          type="number"
                          step="0.01"
                          value={form.amounts[sym] || ''}
                          onChange={e => setForm(p => ({ ...p, amounts: { ...p.amounts, [sym]: e.target.value } }))}
                          placeholder="0.00"
                          style={{ flex: 2 }}
                        />
                        <select
                          value={form.methods[methodKey]}
                          onChange={e => setForm(p => ({ ...p, methods: { ...p.methods, [methodKey]: e.target.value } }))}
                          style={{ flex: 1, minWidth: 130 }}
                        >
                          {methodOptions.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                        </select>
                      </div>
                    </div>
                  );
                })}

                {/* Date */}
                <div className="form-group">
                  <label>{t('common.date')} <span className="required">*</span></label>
                  <input type="date" value={form.date} onChange={set('date')} />
                </div>

                {/* Payment Type */}
                <div className="form-group form-full">
                  <label>{t('cr.paymentType')} <span className="required">*</span></label>
                  {!showNewType ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <select value={form.fund_category_id} onChange={set('fund_category_id')} style={{ flex: 1 }}>
                        <option value="">{t('form.selectType')}</option>
                        {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                      <button type="button" className="btn btn-secondary" style={{ whiteSpace: 'nowrap' }} onClick={() => setShowNewType(true)}>
                        <FiPlus /> {t('cr.newType')}
                      </button>
                    </div>
                  ) : (
                    <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#4f46e5', marginBottom: 8 }}>{t('cr.addNewPaymentType')}</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input value={newTypeName} onChange={e => setNewTypeName(e.target.value)} placeholder={t('cr.typeName')} autoFocus style={{ flex: 1 }} />
                        <button className="btn btn-sm btn-primary" onClick={handleAddType} disabled={savingType}>{savingType ? '...' : t('common.add')}</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => { setShowNewType(false); setNewTypeName(''); }}>{t('common.cancel')}</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* v1.9.128 — Reason input shown only when the "Others" fund is
                    picked. Saves to form.notes (existing column), so the CR
                    detail page, Cash Book, and prints already surface it. */}
                {isOthersFund && (
                  <div className="form-group form-full">
                    <label>
                      {t('cr.reasonLabel', { defaultValue: 'What is this payment for?' })} <span className="required">*</span>
                    </label>
                    <input
                      value={form.notes || ''}
                      onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                      placeholder={t('cr.reasonPlaceholder', { defaultValue: 'e.g. Bible stand donation · chair rental · funeral support' })}
                      autoFocus
                      style={{ border: '1px solid #fcd34d', background: '#fffbeb' }}
                    />
                    <small style={{ color: '#92400e', fontSize: 11, marginTop: 4, display: 'block' }}>
                      {t('cr.reasonHint', { defaultValue: 'Required because "Others" doesn\'t say what the money was for.' })}
                    </small>
                  </div>
                )}

                {/* v1.6.0 — apply-to-outstanding checkbox. Only shows when the
                    picked member actually has an unpaid legacy balance. */}
                {form.member_id && (pickedOutstanding.usd > 0 || pickedOutstanding.kwacha > 0) && (
                  <div className="form-group form-full">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fffbeb', border: '1px solid #fde68a', padding: '10px 12px', borderRadius: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!form.apply_to_outstanding}
                        onChange={e => setForm(p => ({ ...p, apply_to_outstanding: e.target.checked }))}
                      />
                      <div>
                        <div style={{ fontWeight: 700, color: '#92400e', fontSize: 13 }}>
                          {t('cr.applyToOutstanding', { defaultValue: 'Apply this receipt to outstanding balance' })}
                        </div>
                        <div style={{ fontSize: 12, color: '#78350f' }}>
                          {t('cr.owedNow', { defaultValue: 'Owed:' })}{' '}
                          {pickedOutstanding.usd > 0 && <strong>${parseFloat(pickedOutstanding.usd).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>}
                          {pickedOutstanding.usd > 0 && pickedOutstanding.kwacha > 0 && ' · '}
                          {pickedOutstanding.kwacha > 0 && <strong>K{parseFloat(pickedOutstanding.kwacha).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>}
                        </div>
                      </div>
                    </label>
                  </div>
                )}

              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowAdd(false); setShowNewType(false); }}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? t('common.saving') : t('cr.saveCr')}</button>
            </div>
          </div>
        </div>
      )}
      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default CR;
